import React from 'react';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import type {
  IconDefinition,
  IconName,
  IconPrefix,
  SizeProp,
} from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon, type FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import Tooltip, { type TooltipProps } from '@mui/material/Tooltip';
import clsx from 'clsx';
import { faIconConfig } from '../shared/faIconConfig';
import mergeSxProps from '../shared/mergeSxProps';

export type { IconPrefix };

/** Maps Font Awesome SizeProp to a CSS length (em) for a square canvas. */
const sizeToEm = (size: FaIconSize | undefined): string => {
  if (size == null) return '1em';
  const map: Record<string, string> = {
    '2xs': '0.625em',
    xs: '0.75em',
    sm: '0.875em',
    md: '1em',
    lg: '1.25em',
    xl: '1.5em',
    '2xl': '2em',
    '1x': '1em',
    '2x': '2em',
    '3x': '3em',
    '4x': '4em',
    '5x': '5em',
    '6x': '6em',
    '7x': '7em',
    '8x': '8em',
    '9x': '9em',
    '10x': '10em',
  };
  return map[size] ?? '1em';
};

/** Font Awesome sizes plus `md` (1em) between `sm` and `lg`. */
export type FaIconSize = SizeProp | 'md';

/**
 * Kit lookup map (same shape Font Awesome kits export as `byPrefixAndName`).
 * Values are treated as IconDefinition at use sites; kept structural to avoid
 * `@fortawesome/fontawesome-common-types` patch skew between kits and svg-core.
 */
export type FaIconByPrefixAndName = {
  readonly [prefix: string]: Readonly<Record<string, IconDefinition>> | undefined;
};

export type FaIconProps = {
  icon: string;
  prefix?: IconPrefix;
  size?: FaIconSize;
  color?: string;
  sx?: SxProps<Theme>;
  className?: string;
  faProps?: FontAwesomeIconProps;
  title?: string;
  widthAuto?: boolean;
  square?: boolean;
  tooltipProps?: Partial<TooltipProps>;
  /** Injected kit lookup; wrappers pass their kit's `byPrefixAndName`. */
  byPrefixAndName: FaIconByPrefixAndName;
};

/**
 * Resolves an icon from the injected kit map only.
 * @param byPrefixAndName Kit lookup map.
 * @param prefix Font Awesome prefix after solid-/light- rewrite.
 * @param iconName Resolved icon name.
 * @returns Matching IconDefinition, or undefined when the kit lacks it.
 */
function resolveIconDefinition(
  byPrefixAndName: FaIconByPrefixAndName,
  prefix: IconPrefix,
  iconName: IconName,
): IconDefinition | undefined {
  return byPrefixAndName[prefix]?.[iconName];
}

/**
 * Kit-injected Font Awesome icon with MUI Box canvas, optional tooltip, and size map.
 * Call sites should use `FaIconUvilo` (or a package wrapper), not this base directly.
 */
function FaIcon({
  icon: iconProp,
  prefix: prefixProp,
  size,
  color,
  sx,
  className,
  faProps,
  title,
  widthAuto,
  square,
  tooltipProps,
  byPrefixAndName,
}: FaIconProps): React.ReactNode {
  'use memo';

  let icon = iconProp;
  let prefix = prefixProp;
  const requestedKitIcon =
    icon.startsWith('thin-') ||
    icon.startsWith('light-') ||
    icon.startsWith('regular-') ||
    icon.startsWith('solid-')
      ? icon
      : null;

  if (icon.startsWith('mdi:')) {
    console.error(`icon ${icon} is not supported by FontAwesomeIcon`);
    return null;
  }
  prefix = prefix ?? faIconConfig.prefix;

  if (requestedKitIcon !== null) {
    icon = icon.replace(
      /thin-|light-|regular-|solid-/,
      prefix === 'fat'
        ? 'thin-'
        : prefix === 'fal'
          ? 'light-'
          : prefix === 'far'
            ? 'regular-'
            : 'solid-',
    );
    prefix = 'fak';
  }

  let iconDefinition = resolveIconDefinition(
    byPrefixAndName,
    prefix as IconPrefix,
    icon as IconName,
  );

  // Kit icons that ship only one weight (e.g. solid-zai) keep that weight when
  // the theme rewrite has no matching light-/thin-/regular- variant.
  if (
    !iconDefinition &&
    requestedKitIcon !== null &&
    icon !== requestedKitIcon &&
    prefix === 'fak'
  ) {
    iconDefinition = resolveIconDefinition(byPrefixAndName, 'fak', requestedKitIcon as IconName);
  }

  if (!iconDefinition) {
    console.info(`"${icon}"   => icon not found in "${prefix}" prefix`);
    iconDefinition = resolveIconDefinition(
      byPrefixAndName,
      faIconConfig.prefix,
      'square-question' as IconName,
    );
    if (!iconDefinition) return null;
  }

  /** Size prop (or explicit square) drives an em-based canvas so FA v7 SVGs scale correctly. */
  const sizedCanvas = square === true || size != null;
  const dimension = sizedCanvas ? sizeToEm(size) : undefined;
  /** `md` is a FaIcon-only step; Font Awesome has no matching size class. */
  const faSize: SizeProp | undefined = size === 'md' ? undefined : size;

  const component = (
    <Box
      className={clsx('fa-icon', className)}
      component="span"
      sx={mergeSxProps(
        {
          color,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
          ...(dimension != null && {
            width: dimension,
            height: dimension,
          }),
          ...(sizedCanvas && {
            // FA v7 defaults SVG canvas to ~20px via --fa-width; fill the sized wrapper (not 1em of inherited text).
            '& svg.svg-inline--fa': {
              width: '100%',
              height: '100%',
              '--fa-width': '100%',
            },
          }),
        },
        sx,
      )}
    >
      <FontAwesomeIcon
        icon={iconDefinition}
        className="fa-icon"
        {...faProps}
        size={sizedCanvas ? faProps?.size : (faProps?.size ?? faSize)}
        widthAuto={widthAuto ?? sizedCanvas}
      />
    </Box>
  );

  return title ? (
    <Tooltip
      title={title}
      {...tooltipProps}
      className={clsx('fa-icon', className, tooltipProps?.className)}
    >
      {component}
    </Tooltip>
  ) : (
    component
  );
}

export default FaIcon;
